import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

export default function SignupPage() {
  const { signup } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please fill in all fields'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await signup(email, password, name);
      toast.success('Account created! Let\'s build your routine.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[--hc-bg] flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-white flex-col justify-center items-center px-16 border-r border-[--hc-border]">
        <img src="/logo.png" alt="HabitCloser" className="h-16 w-auto object-contain" />
        <p className="text-sm text-[--hc-text-muted] mt-6 max-w-md leading-relaxed text-center">
          Build a consistent morning operating routine so you take the right business actions every day before distractions.
        </p>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px]">
          <div className="lg:hidden mb-8">
            <img src="/logo.png" alt="HabitCloser" className="h-10 w-auto" />
          </div>

          <h2 className="text-2xl font-semibold text-[--hc-text] mb-1">Create your account</h2>
          <p className="text-sm text-[--hc-text-muted] mb-8">Start building your operating routine.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[--hc-text] mb-1.5">Name</label>
              <input
                data-testid="signup-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring] transition-shadow"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[--hc-text] mb-1.5">Email</label>
              <input
                data-testid="signup-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring] transition-shadow"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[--hc-text] mb-1.5">Password</label>
              <div className="relative">
                <input
                  data-testid="signup-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-[--hc-border] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[--hc-ring] transition-shadow pr-10"
                  placeholder="At least 6 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[--hc-text-muted] hover:text-[--hc-text]"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              data-testid="signup-submit-button"
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-xl bg-[--hc-primary] text-white text-sm font-medium hover:bg-[--hc-primary-hover] active:bg-[--hc-primary-active] active:scale-[0.98] transition-colors duration-150 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </form>

          <p className="text-sm text-[--hc-text-muted] mt-6 text-center">
            Already have an account?{' '}
            <Link to="/login" className="text-[--hc-primary] font-medium hover:underline" data-testid="signup-login-link">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
